import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TeacherPageHeader from '../../components/TeacherPageHeader';
import { useAuth } from '../../hooks/useAuth';
import { authAPI, calendarAPI, holidayAPI, practiceAPI } from '../../services';
import toast from 'react-hot-toast';
import { User, Holiday } from '../../types/api';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import Modal from '../../components/Modal';
import { useLanguage } from '../../contexts/LanguageContext';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ko, enUS } from 'date-fns/locale';
import { useNotifications } from '../../hooks/notification/useNotifications';
import { PublicHolidayInfo } from '../../components/SimpleCalendar';
import { CalendarEvent } from '../../types/api';
import { Notification } from '../../types/notification';
import { UnifiedScheduleEvent } from '../../services/calendarAPI';
import { DEFAULT_TEACHER_ID } from '../../config/constants';

const Dashboard: React.FC = () => {
  const { auth, logout, isAuthenticated, userInfo, isUser, updateUserInfo } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const dateLocale = language === 'ENG' ? enUS : ko;

  // 페이지 마운트 시 상단으로 스크롤
  useScrollToTop();

  const [loading, setLoading] = useState(true);
  const [showNotiModal, setShowNotiModal] = useState(false);
  const [selectedNoti, setSelectedNoti] = useState<Notification | null>(null);

  // 알림 관리 훅 사용
  const {
    notifications,
    markAsRead,
    loadNotifications,
  } = useNotifications('user', { autoLoad: false });

  // 캘린더 관련 상태
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [selectedDate] = useState<Date>(new Date());
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [publicHolidaysMap, setPublicHolidaysMap] = useState<Map<string, PublicHolidayInfo>>(new Map());

  // SimpleCalendar에서 공휴일 데이터 전달받기
  const handlePublicHolidaysLoad = useCallback((holidaysMap: Map<string, PublicHolidayInfo>) => {
    setPublicHolidaysMap(holidaysMap);
  }, []);

  useEffect(() => {
    // 인증 상태가 변경될 때만 실행 (페이지 로드 시 한 번만)
    if (isAuthenticated && isUser && !userInfo) {
      const fetchUserInfo = async () => {
        try {
          setLoading(true);

          const response = await authAPI.verifyToken();
          if (response.success && response.data) {
            const { user: newUserInfo } = response.data;
            updateUserInfo(newUserInfo);
          }
        } catch (error: unknown) {
          const errorMessage = (error as Error).message || t('user_info_load_failed');
          alert(`${errorMessage} ${t('please_login_again')}`);
          logout();
        } finally {
          setLoading(false);
        }
      };

      fetchUserInfo();
    } else if (!isAuthenticated) {
      navigate('/login');
    } else if (!isUser) {
      navigate('/admin/dashboard');
    } else if (userInfo) {
      // 이미 userInfo가 있으면 로딩 상태만 해제
      setLoading(false);
    }
  }, [isAuthenticated, isUser, userInfo]); // userInfo 의존성 추가

  // 알림 로드
  useEffect(() => {
    if (auth.isAuthenticated && auth.userType === 'user') {
      loadNotifications();
    }
  }, [auth.isAuthenticated, auth.userType, loadNotifications]);

  // 캘린더 데이터 로드 (userInfo가 있을 때만)
  useEffect(() => {
    if (auth.isAuthenticated && auth.userType === 'user' && userInfo) {
      loadCalendarData();
    }
  }, [auth.isAuthenticated, auth.userType, userInfo]);

  // 학원 휴일 조회
  const fetchHolidays = useCallback(async (date: Date) => {
    try {
      const response = await holidayAPI.getHolidays({
        year: date.getFullYear(),
        month: date.getMonth() + 1
      });
      if (response.data?.success && response.data?.data) {
        setHolidays(response.data.data);
      }
    } catch {
      // 휴일 조회 실패
    }
  }, []);

  // 월 변경 시 휴일 다시 조회
  useEffect(() => {
    fetchHolidays(currentMonth);
  }, [currentMonth, fetchHolidays]);

  // 캘린더 데이터 로드
  const loadCalendarData = async (targetDate?: Date) => {
    if (!userInfo) return;

    try {
      const baseDate = targetDate || new Date();
      const startDate = format(startOfMonth(baseDate), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(baseDate), 'yyyy-MM-dd');

      // 수업 일정 조회
      const scheduleResponse = await calendarAPI.getUnifiedCalendar({
        startDate,
        endDate,
        teacherId: DEFAULT_TEACHER_ID,
        userId: (userInfo as User).id,
        includeRegular: true,
        includeModifications: true
      });

      // 연습실 예약 조회 (practiceAPI 사용)
      interface PracticeBookingData {
        id: number;
        startTime: string;
        endTime: string;
        practiceRoom: string;
        status?: string;
      }
      let practiceBookings: PracticeBookingData[] = [];
      try {
        const practiceResponse = await fetch(`/api/practice/my-sessions?startDate=${startDate}&endDate=${endDate}`, {
          credentials: 'include'  // 쿠키 기반 인증 사용
        });
        if (practiceResponse.ok) {
          const practiceData = await practiceResponse.json();
          if (practiceData.success && practiceData.data) {
            practiceBookings = practiceData.data.sessions || [];
          }
        }
      } catch {
        // Practice bookings load failed silently
      }

      const response = scheduleResponse;

      if (response.data && response.data.success) {
        // API 응답: events는 { date, schedules: [...] }[] 구조
        const eventsByDate = response.data.data.events || [];

        // 모든 schedules를 flat하게 추출
        const allSchedules: UnifiedScheduleEvent[] = [];
        eventsByDate.forEach((dayEvent: { date: string; schedules?: UnifiedScheduleEvent[] }) => {
          if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
            dayEvent.schedules.forEach((schedule: UnifiedScheduleEvent) => {
              allSchedules.push({
                ...schedule,
                date: dayEvent.date
              });
            });
          }
        });

        // CalendarEvent 형식으로 변환
        const calendarEventsList: CalendarEvent[] = [];

        // 수업 일정 추가
        allSchedules.forEach((s) => {
          const eventDate = new Date(s.date);
          const [startHour, startMinute] = s.startTime.split(':');
          const [endHour, endMinute] = s.endTime.split(':');

          const startTime = new Date(eventDate);
          startTime.setHours(parseInt(startHour), parseInt(startMinute), 0);

          const endTime = new Date(eventDate);
          endTime.setHours(parseInt(endHour), parseInt(endMinute), 0);

          calendarEventsList.push({
            id: `lesson_${s.id}`,
            title: s.title || s.studentName || t('lesson_default_title'),
            type: 'lesson',
            start: startTime,
            end: endTime,
            color: s.scheduleType === 'regular' ? '#4CAF50' :
                   s.scheduleType === 'rescheduled' ? '#FF9800' :
                   s.scheduleType === 'cancelled' ? '#F44336' : '#2196F3',
            data: { ...s, eventType: 'lesson' }
          });
        });

        // 연습실 예약 추가 (취소된 예약 제외)
        practiceBookings
          .filter((booking: PracticeBookingData) => booking.status !== 'cancelled')
          .forEach((booking: PracticeBookingData) => {
            const startTime = new Date(booking.startTime);
            const endTime = new Date(booking.endTime);

            calendarEventsList.push({
              id: `practice_${booking.id}`,
              title: `${t('practice_room_booking_label')} (${booking.practiceRoom})`,
              type: 'practice',
              start: startTime,
              end: endTime,
              color: '#9C27B0',  // 보라색
              data: { ...booking, eventType: 'practice' }
            });
          });

        // 납부일 이벤트 추가
        if (userInfo && (userInfo as User).paymentDayOfMonth) {
          const paymentDay = (userInfo as User).paymentDayOfMonth!;
          const baseDate = targetDate || new Date();
          const paymentDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), paymentDay);

          // 해당 월에 유효한 날짜인지 확인 (예: 31일이 없는 달)
          if (paymentDate.getMonth() === baseDate.getMonth()) {
            calendarEventsList.push({
              id: `payment_${paymentDate.toISOString()}`,
              title: t('payment_day_event'),
              type: 'personal',
              start: paymentDate,
              end: paymentDate,
              color: '#E91E63',  // 핑크색
              data: { eventType: 'payment', paymentDay }
            });
          }
        }

        setCalendarEvents(calendarEventsList);
      }
    } catch {
      // Calendar data load failed silently
    }
  };

  const openNoti = async (n: Notification) => {
    setSelectedNoti(n);
    setShowNotiModal(true);
    if (!n.isRead) {
      await markAsRead(n.id);
      // 읽음 처리 후 알림 목록 다시 로드
      await loadNotifications();
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  // 연습실 예약 취소 핸들러
  const handleCancelPracticeBooking = async (sessionId: string | number) => {
    if (!window.confirm(t('confirm_cancel_booking'))) {
      return;
    }
    try {
      const response = await practiceAPI.cancelBooking(String(sessionId));
      if (response.success) {
        toast.success(t('booking_cancelled'));
        loadCalendarData(currentMonth);
      } else {
        toast.error(response.message || t('cancel_booking_failed'));
      }
    } catch {
      toast.error(t('cancel_booking_error'));
    }
  };

  if (loading) {
    return (
      <div className="user-dashboard">
        <div className="page-content">
          <div className="container">
            <div className="loading">{t('loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated || auth.userType !== 'user') {
    return (
      <div className="user-dashboard">
        <div className="page-content">
          <div className="container">
            <div className="error">{t('access_denied')}</div>
          </div>
        </div>
      </div>
    );
  }

  const user = userInfo as User;

  const renderNotificationTitle = (noti: Notification) => {
    const titleMap: Record<string, string> = {
      practice_booked: t('notification_practice_booked_title'),
      lesson_request_approved: t('notification_lesson_approved_title'),
      lesson_request_rejected: t('notification_lesson_rejected_title'),
      modification_approved: t('notification_schedule_approved_title'),
      modification_rejected: t('notification_schedule_rejected_title'),
      payment_due: t('notification_payment_due_title'),
    };
    return titleMap[noti.category] || (user?.isForeigner ? noti.titleEn : noti.title);
  };

  const renderNotificationMessage = (noti: Notification) => {
    const simpleMessageMap: Record<string, string> = {
      practice_booked: t('notification_practice_booked_message'),
      lesson_request_approved: t('notification_lesson_approved_message'),
      lesson_request_rejected: t('notification_lesson_rejected_message'),
    };
    if (simpleMessageMap[noti.category]) return simpleMessageMap[noti.category];
    const message = user?.isForeigner ? noti.messageEn : noti.message;
    return message ? <div style={{ whiteSpace: 'pre-line' }}>{message}</div> : null;
  };

  const renderNotificationMeta = (noti: Notification) => {
    const metaMap: Record<string, string> = {
      modification_approved: t('schedule_change_approved'),
      modification_rejected: t('schedule_change_rejected'),
      practice_booked: t('practice_room_booking_completed'),
      lesson_request_approved: t('lesson_request_approved'),
      lesson_request_rejected: t('lesson_request_rejected'),
      payment_due: t('payment_due_notice'),
    };
    return metaMap[noti.category] || '';
  };

  // ── 시안 홈: 파생 데이터 ──
  const now = new Date();
  const isCancelledEvent = (e: CalendarEvent) => ((e.data as any)?.scheduleType === 'cancelled');
  const lessonEvents = calendarEvents.filter(e => e.type === 'lesson');
  const upcomingLessons = lessonEvents
    .filter(e => !isCancelledEvent(e) && new Date(e.start) > now)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const nextLesson = upcomingLessons[0];
  const monthLessons = lessonEvents.filter(e => !isCancelledEvent(e));
  const doneLessonCount = monthLessons.filter(e => new Date(e.end) <= now).length;
  const nextPractice = calendarEvents
    .filter(e => e.type === 'practice' && new Date(e.start) > now)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())[0];
  const paymentDay = user?.paymentDayOfMonth;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const paymentDate = paymentDay
    ? (() => {
        const d = new Date(now.getFullYear(), now.getMonth(), paymentDay);
        return d.getTime() < todayStart.getTime()
          ? new Date(now.getFullYear(), now.getMonth() + 1, paymentDay)
          : d;
      })()
    : null;
  // 이번 달 수업 + 연습실 (지난 건 그레이 처리)
  const monthItems = calendarEvents
    .filter(e => (e.type === 'lesson' || e.type === 'practice') && !isCancelledEvent(e))
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const changedItems = lessonEvents
    .filter(e => ['rescheduled', 'cancelled'].includes((e.data as any)?.scheduleType))
    .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())
    .slice(0, 4);

  return (
    <div className="user-dashboard">
      <TeacherPageHeader
        title="홈"
        subtitle={format(new Date(), language === 'ENG' ? 'MMM d, yyyy (E)' : 'yyyy. M. d (E)', { locale: dateLocale })}
      />
      <div className="page-content">
        <div className="container">
          {/* 스탯 카드 4개 (시안) */}
          <div className="home-stats">
            <div className="stat-card navy">
              <div className="stat-label">다음 수업</div>
              <div className="stat-value">
                {nextLesson
                  ? format(new Date(nextLesson.start), language === 'ENG' ? 'M. d (E) HH:mm' : 'M. d (E) HH:mm', { locale: dateLocale })
                  : '—'}
              </div>
              <div className="stat-meta">
                {nextLesson
                  ? `${format(new Date(nextLesson.start), 'HH:mm')}–${format(new Date(nextLesson.end), 'HH:mm')} · ${nextLesson.title}`
                  : '예정된 수업이 없습니다'}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">이번 달 수업</div>
              <div className="stat-value">
                {doneLessonCount}회 <span className="stat-sub">/ {monthLessons.length}회</span>
              </div>
              <div className="stat-meta">출석 {doneLessonCount} · 남은 수업 {Math.max(monthLessons.length - doneLessonCount, 0)}</div>
            </div>
            <div className="stat-card pay">
              <div className="stat-label">수강료 납부일</div>
              <div className="stat-value pay-value">
                {paymentDate ? format(paymentDate, 'M. d (E)', { locale: dateLocale }) : '—'}
              </div>
              <div className="stat-meta">
                {(user as any)?.monthlyFee
                  ? `월 ${Number((user as any).monthlyFee).toLocaleString()}원`
                  : paymentDate ? `매월 ${paymentDay}일` : '납부일 미설정'}
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">연습실 예약</div>
              <div className="stat-value">
                {nextPractice ? format(new Date(nextPractice.start), 'M. d (E) HH:mm', { locale: dateLocale }) : '—'}
              </div>
              <div className="stat-meta">
                {nextPractice
                  ? `${format(new Date(nextPractice.start), 'HH:mm')}–${format(new Date(nextPractice.end), 'HH:mm')} · 예약 확정`
                  : '예약된 연습실이 없습니다'}
              </div>
            </div>
          </div>

          {/* 3단 리스트 (시안) */}
          <div className="home-columns">
            <div className="home-card week-card">
              <div className="card-head">
                <span>이번 달 수업 · 연습실</span>
                <span className="head-meta">
                  {format(startOfMonth(now), 'M. d')} – {format(endOfMonth(now), 'M. d')}
                </span>
              </div>
              <div className="card-rows">
              {monthItems.length === 0 ? (
                <div className="empty-row">이번 달 일정이 없습니다</div>
              ) : (
                monthItems.map((e, i) => {
                  const done = new Date(e.end) <= now;
                  const isPractice = e.type === 'practice';
                  return (
                    <div className={`week-row ${done ? 'past' : ''}`} key={i}>
                      <div className="week-date">
                        <div className="week-day">{format(new Date(e.start), 'E', { locale: dateLocale })}</div>
                        <div className="week-num">{format(new Date(e.start), 'd')}</div>
                      </div>
                      <div className="week-bar" style={{ background: isPractice ? '#0f9d76' : '#1e56d6' }} />
                      <div className="week-info">
                        <div className="week-title">{e.title}</div>
                        <div className="week-meta">
                          {format(new Date(e.start), 'HH:mm')}–{format(new Date(e.end), 'HH:mm')}
                          {done && ' · 출석 완료'}
                        </div>
                      </div>
                      <span className={`week-status ${done ? 'done' : isPractice ? 'confirmed' : 'planned'}`}>
                        {done ? '완료' : isPractice ? '확정' : '예정'}
                      </span>
                    </div>
                  );
                })
              )}
              </div>
            </div>

            <div className="home-card changes-card">
              <div className="card-head"><span>변경 · 취소된 수업</span></div>
              {changedItems.length === 0 ? (
                <div className="empty-row">변경/취소된 수업이 없습니다</div>
              ) : (
                changedItems.map((e, i) => {
                  const isCancel = (e.data as any)?.scheduleType === 'cancelled';
                  return (
                    <div className="change-row" key={i}>
                      <span className={`change-tag ${isCancel ? 'cancel' : 'change'}`}>{isCancel ? '취소' : '변경'}</span>
                      <div className="change-info">
                        <div className="change-title">
                          {format(new Date(e.start), 'M. d (E) HH:mm', { locale: dateLocale })} · {e.title}
                        </div>
                        <div className="change-meta">{isCancel ? '취소된 수업입니다' : '변경된 일정으로 진행됩니다'}</div>
                      </div>
                    </div>
                  );
                })
              )}
              <button className="card-foot-link" onClick={() => handleNavigate('/user/lesson-schedule')}>
                일정 변경 신청하기 ›
              </button>
            </div>

            <div className="home-card notices-card">
              <div className="card-head">
                <span>알림</span>
              </div>
              <div className="card-rows">
              {notifications.length === 0 ? (
                <div className="empty-row">{t('no_new_notifications')}</div>
              ) : (
                notifications.map((n) => (
                  <div className={`notice-row ${n.isRead ? 'read' : 'unread'}`} key={n.id} onClick={() => openNoti(n)}>
                    <div className="notice-title">{renderNotificationTitle(n)}</div>
                    <div className="notice-date">{n.createdAt ? format(new Date(n.createdAt), 'M. d HH:mm') : ''}</div>
                  </div>
                ))
              )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 알림 상세 모달 */}
      <Modal
        isOpen={showNotiModal}
        onClose={() => setShowNotiModal(false)}
        title={t('notifications')}
        size="lg"
      >
        {selectedNoti && (
          <>
            <div className="notification-detail-content">
              <div className="notification-detail-title">{renderNotificationTitle(selectedNoti)}</div>
              <div className="notification-detail-message">
                {renderNotificationMessage(selectedNoti)}
                {!selectedNoti.message && !selectedNoti.messageEn && t('no_detail_message')}
              </div>
              <div className="notification-detail-meta">
                {selectedNoti.createdAt ? format(new Date(selectedNoti.createdAt), 'yyyy-MM-dd HH:mm') : ''}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowNotiModal(false)}>{t('confirm')}</button>
              {selectedNoti.category === 'practice_booked' && (
                <button className="btn btn-white" onClick={() => { handleNavigate('/user/practice-booking'); setShowNotiModal(false); }}>{t('go_to_page')}</button>
              )}
              {(selectedNoti.category === 'modification_approved' || selectedNoti.category === 'modification_rejected') && (
                <button className="btn btn-white" onClick={() => { handleNavigate('/user/lesson-schedule'); setShowNotiModal(false); }}>{t('go_to_page')}</button>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard; 